/* global TimeoutId */
import React from 'react';

interface IProps {
  ms: number;
  callback: () => unknown;
  resetKey?: string | number;
  children?: React.ReactNode;
}

interface ITimingProps extends IProps {
  setMethod: typeof setTimeout | typeof setInterval;
  clearMethod: typeof clearTimeout | typeof clearInterval;
}

class Timing extends React.Component<ITimingProps> {
  private trackingId: TimeoutId | undefined;

  public componentDidMount() {
    this.start();
  }

  public componentDidUpdate(prevProps: ITimingProps) {
    // Assumes that setMethod/clearMethod props will never change. This is only okay because the
    // <Timing> component is not exported directly.
    if (this.props.resetKey !== prevProps.resetKey) {
      this.stop();
      this.start();
    }
  }

  public componentWillUnmount() {
    this.stop();
  }

  public render() {
    return this.props.children || null;
  }

  private triggerCallback = () => {
    this.props.callback();
  };

  private start() {
    const { setMethod } = this.props;
    // Cannot call this.props.setMethod or else setTimeout/setInterval will have
    // the wrong `this` value and throw an error.
    this.trackingId = setMethod(this.triggerCallback, this.props.ms);
  }

  private stop() {
    if (this.trackingId) {
      // Cannot call this.props.clearMethod or else clearTimeout/clearInterval will have
      // the wrong `this` value and throw an error.
      const { clearMethod } = this.props;
      clearMethod(this.trackingId);
    }
    this.trackingId = undefined;
  }
}

export const Interval: React.SFC<IProps> = (props) => (
  <Timing setMethod={setInterval} clearMethod={clearInterval} {...props} />
);

export const Timeout: React.SFC<IProps> = (props) => (
  <Timing setMethod={setTimeout} clearMethod={clearTimeout} {...props} />
);
